import type { PartialDeep } from 'type-fest';
import { ANY_LOCKER_VALUE, AVAILABLE_STATUS, SORT_KEYS } from 'contexts/preferences/listing-params/types';
import type { Filter, PropertyType, PropertyTypeFilter, Sort, Status } from 'contexts/preferences/listing-params/types';
import type { MatchSpan, ParsedLocker, ParsedSearchFilters } from 'utils/natural-language-filter-parser/types';
import { formatI18nCompactPrice, getCurrencyFromLocale } from 'utils/i18n-format-currency';
import {
  ALL_HOME_TYPES,
  ANY_COUNT_VALUE,
  EMPTY_HOME_TYPE,
  HOME_TYPE_LABELS,
  LOCKER_LABELS,
  MIN_PLACE_FRAGMENT_LENGTH,
  SORT_LABELS,
  STATUS_LABELS,
} from './smart-search.constants';
import { LOCATION_FIELDS, type SmartSearchLocation, type SmartSearchTokenModel } from './smart-search.types';

const HOME_TYPE_TOKEN_PREFIX = 'homeType:';

/** Words that end a place name and begin the next thing the query asks for. */
const PLACE_FRAGMENT_TERMINATORS =
  /\s(?:with|without|under|over|below|above|and|that|which|having|priced|between|for)\b/i;

const BOOLEAN_FEATURE_LABELS: Record<string, string> = {
  garage: 'Garage',
  pool: 'Pool',
  fireplace: 'Fireplace',
  waterfront: 'Waterfront',
  openHouse: 'Open house',
};

type BooleanFeature = keyof typeof BOOLEAN_FEATURE_LABELS;

const BOOLEAN_FEATURES = Object.keys(BOOLEAN_FEATURE_LABELS) as BooleanFeature[];

/** Every home type selected reads as "no preference", so it is never a chip. */
const ALL_HOME_TYPES_COUNT = Object.keys(ALL_HOME_TYPES).length;

function formatPrice(value: number, locale: string): string {
  return formatI18nCompactPrice(value, locale, getCurrencyFromLocale(locale));
}

function formatDate(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

function selectedHomeTypes(homeType: ParsedSearchFilters['homeType']): PropertyType[] {
  if (!homeType) {
    return [];
  }
  return (Object.keys(homeType) as PropertyType[]).filter(type => homeType[type]);
}

/** Pulls the location fields out of a parse result. */
export function extractLocation(filters: ParsedSearchFilters): SmartSearchLocation {
  const location: SmartSearchLocation = {};
  for (const field of LOCATION_FIELDS) {
    const value = filters[field];
    if (value) {
      location[field] = value;
    }
  }
  return location;
}

/**
 * Turns a parse result into the chips shown under the input.
 *
 * Every label reads as a complete phrase ("3+ Beds", not "Beds 3+") so a chip
 * makes sense on its own. Count filters sitting at their "any" sentinel are
 * skipped so they do not render as a meaningless chip, matching how the filter
 * bar treats `0+`.
 */
export function buildSmartSearchTokens(
  filters: ParsedSearchFilters,
  locale = 'en-CA'
): SmartSearchTokenModel[] {
  const tokens: SmartSearchTokenModel[] = [];

  for (const type of selectedHomeTypes(filters.homeType)) {
    tokens.push({
      id: `${HOME_TYPE_TOKEN_PREFIX}${type}`,
      label: HOME_TYPE_LABELS[type],
    });
  }

  if (filters.bedrooms && filters.bedrooms !== ANY_COUNT_VALUE) {
    tokens.push({ id: 'bedrooms', label: `${filters.bedrooms} Beds` });
  }

  if (filters.bathrooms && filters.bathrooms !== ANY_COUNT_VALUE) {
    tokens.push({ id: 'bathrooms', label: `${filters.bathrooms} Baths` });
  }

  if (filters.parkingSpaces && filters.parkingSpaces !== ANY_COUNT_VALUE) {
    tokens.push({ id: 'parkingSpaces', label: `${filters.parkingSpaces} Parking` });
  }

  const { priceMin, priceMax } = filters;
  if (priceMin !== undefined || priceMax !== undefined) {
    let label: string;
    if (priceMin !== undefined && priceMax !== undefined) {
      label = `${formatPrice(priceMin, locale)} - ${formatPrice(priceMax, locale)}`;
    } else if (priceMax !== undefined) {
      label = `Under ${formatPrice(priceMax, locale)}`;
    } else {
      label = `Over ${formatPrice(priceMin as number, locale)}`;
    }
    tokens.push({ id: 'price', label, tone: 'accent' });
  }

  const { sqftMin, sqftMax } = filters;
  if (sqftMin !== undefined || sqftMax !== undefined) {
    let label: string;
    if (sqftMin !== undefined && sqftMax !== undefined) {
      label = `${sqftMin} - ${sqftMax} sqft`;
    } else if (sqftMax !== undefined) {
      label = `Under ${sqftMax} sqft`;
    } else {
      label = `Over ${sqftMin} sqft`;
    }
    tokens.push({ id: 'sqft', label });
  }

  for (const feature of BOOLEAN_FEATURES) {
    if (filters[feature]) {
      tokens.push({ id: feature, label: BOOLEAN_FEATURE_LABELS[feature] });
    }
  }

  if (filters.rental !== undefined) {
    tokens.push({ id: 'rental', label: filters.rental ? 'For rent' : 'For sale' });
  }

  if (filters.status) {
    tokens.push({
      id: 'status',
      label: STATUS_LABELS[filters.status] || filters.status,
    });
  }

  if (filters.listedSince) {
    tokens.push({ id: 'listedSince', label: `Listed after ${formatDate(filters.listedSince)}` });
  }

  if (filters.listedTo) {
    tokens.push({ id: 'listedTo', label: `Listed before ${formatDate(filters.listedTo)}` });
  }

  const locker = filters.additional?.condoOrTownhouse?.locker;
  if (locker && locker !== 'any') {
    tokens.push({ id: 'locker', label: LOCKER_LABELS[locker] || locker });
  }

  const maintenanceFee = filters.additional?.condoOrTownhouse?.maintenanceFee;
  if (maintenanceFee !== undefined && maintenanceFee !== null) {
    tokens.push({
      id: 'maintenanceFee',
      label: `Maint. fee under ${formatPrice(maintenanceFee, locale)}`,
    });
  }

  if (filters.sort) {
    tokens.push({ id: 'sort', label: SORT_LABELS[filters.sort] || filters.sort });
  }

  if (filters.hasImage) {
    tokens.push({ id: 'hasImage', label: 'Has photos' });
  }

  if (filters.showComingSoon) {
    tokens.push({ id: 'showComingSoon', label: 'Coming soon' });
  }

  for (const field of LOCATION_FIELDS) {
    const value = filters[field];
    if (value) {
      tokens.push({ id: field, icon: '📍', label: value, tone: 'accent', isLocation: true });
    }
  }

  // Filter chips read as a light-blue set (the `accent` tone). Tokens that
  // already declare a tone keep it — the place chip stays `muted` so it reads as
  // a destination rather than a filter.
  return tokens.map(token => (token.tone ? token : { ...token, tone: 'accent' }));
}

/**
 * Drops one token's contribution from a parse result.
 *
 * Returns the original object when the id is unknown so callers can compare by
 * reference to detect a no-op.
 */
export function removeTokenFromFilters(
  filters: ParsedSearchFilters,
  tokenId: string
): ParsedSearchFilters {
  const next: ParsedSearchFilters = { ...filters };

  if (tokenId.startsWith(HOME_TYPE_TOKEN_PREFIX)) {
    const type = tokenId.slice(HOME_TYPE_TOKEN_PREFIX.length) as PropertyType;
    if (!next.homeType?.[type]) {
      return filters;
    }
    const homeType = { ...next.homeType, [type]: false };
    if (selectedHomeTypes(homeType).length === 0) {
      delete next.homeType;
    } else {
      next.homeType = homeType;
    }
    return next;
  }

  switch (tokenId) {
  case 'price':
    delete next.priceMin;
    delete next.priceMax;
    return next;
  case 'sqft':
    delete next.sqftMin;
    delete next.sqftMax;
    return next;
  case 'locker': {
    const condoOrTownhouse = { ...next.additional?.condoOrTownhouse };
    delete condoOrTownhouse.locker;
    next.additional = Object.keys(condoOrTownhouse).length > 0 ? { condoOrTownhouse } : undefined;
    return next;
  }
  case 'maintenanceFee': {
    const condoOrTownhouse = { ...next.additional?.condoOrTownhouse };
    delete condoOrTownhouse.maintenanceFee;
    next.additional = Object.keys(condoOrTownhouse).length > 0 ? { condoOrTownhouse } : undefined;
    return next;
  }
  default:
    break;
  }

  if (tokenId in next) {
    delete next[tokenId as keyof ParsedSearchFilters];
    return next;
  }

  return filters;
}

/**
 * Maps a parse result onto a `Filter` patch, mirroring how the chat applies
 * search parameters.
 *
 * Anything present in `previous` but absent from `next` is reset to its neutral
 * value rather than left in place, so removing a token actually clears the
 * filter instead of silently keeping it. Location fields are excluded: the area
 * page owns its own location.
 */
export function toFilterPatch(
  next: ParsedSearchFilters,
  previous: ParsedSearchFilters = {}
): PartialDeep<Filter> {
  const patch: PartialDeep<Filter> = {};

  for (const field of ['bedrooms', 'bathrooms', 'parkingSpaces'] as const) {
    if (next[field] !== undefined) {
      patch[field] = next[field];
    } else if (previous[field] !== undefined) {
      patch[field] = ANY_COUNT_VALUE;
    }
  }

  // "under $900k" can parse a min above the max; the max is the reliable half.
  let priceMin = next.priceMin;
  const priceMax = next.priceMax;
  if (priceMin !== undefined && priceMax !== undefined && priceMin > priceMax) {
    priceMin = undefined;
  }

  if (priceMin !== undefined) {
    patch.priceMin = priceMin;
  } else if (previous.priceMin !== undefined) {
    patch.priceMin = null;
  }

  if (priceMax !== undefined) {
    patch.priceMax = priceMax;
  } else if (previous.priceMax !== undefined) {
    patch.priceMax = null;
  }

  for (const field of ['sqftMin', 'sqftMax'] as const) {
    if (next[field] !== undefined) {
      patch[field] = next[field];
    } else if (previous[field] !== undefined) {
      patch[field] = null;
    }
  }

  for (const field of ['listedSince', 'listedTo'] as const) {
    if (next[field] !== undefined) {
      patch[field] = next[field];
    } else if (previous[field] !== undefined) {
      patch[field] = null;
    }
  }

  // Home type must always go out complete; a partial object would read as
  // "everything else is excluded" downstream.
  if (next.homeType) {
    patch.homeType = { ...EMPTY_HOME_TYPE, ...next.homeType } as PropertyTypeFilter;
  } else if (previous.homeType) {
    patch.homeType = ALL_HOME_TYPES;
  }

  for (const feature of BOOLEAN_FEATURES) {
    if (next[feature] !== undefined) {
      patch[feature] = next[feature];
    } else if (previous[feature] !== undefined) {
      patch[feature] = false;
    }
  }

  if (next.rental !== undefined) {
    patch.rental = next.rental;
  } else if (previous.rental !== undefined) {
    patch.rental = false;
  }

  if (next.status !== undefined) {
    patch.status = next.status as Status;
  } else if (previous.status !== undefined) {
    patch.status = 'available';
  }

  if (next.hasImage !== undefined) {
    patch.hasImage = next.hasImage;
  } else if (previous.hasImage !== undefined) {
    patch.hasImage = false;
  }

  if (next.showComingSoon !== undefined) {
    patch.showComingSoon = next.showComingSoon;
  } else if (previous.showComingSoon !== undefined) {
    patch.showComingSoon = false;
  }

  // Locker and maintenance fee only mean something for condos and townhouses.
  const hasCondoOrTownhouse = Boolean(next.homeType?.condo || next.homeType?.townhouse);
  const parsedAdditional = next.additional?.condoOrTownhouse;

  if (next.homeType && !hasCondoOrTownhouse) {
    patch.additional = { condoOrTownhouse: { locker: 'any', maintenanceFee: null } };
  } else if (parsedAdditional) {
    patch.additional = {
      condoOrTownhouse: {
        locker: parsedAdditional.locker ?? 'any',
        maintenanceFee: parsedAdditional.maintenanceFee ?? null,
      },
    };
  } else if (previous.additional?.condoOrTownhouse) {
    patch.additional = { condoOrTownhouse: { locker: 'any', maintenanceFee: null } };
  }

  return patch;
}

export function fromAppliedFilter(filter?: PartialDeep<Filter>): ParsedSearchFilters {
  if (!filter) {
    return {};
  }

  const parsed: ParsedSearchFilters = {};

  if (filter.homeType) {
    const homeType: PropertyTypeFilter = {
      house: Boolean(filter.homeType.house),
      condo: Boolean(filter.homeType.condo),
      townhouse: Boolean(filter.homeType.townhouse),
      farm: Boolean(filter.homeType.farm),
      land: Boolean(filter.homeType.land),
      commercial: Boolean(filter.homeType.commercial),
    };
    const selectedCount = (Object.keys(homeType) as PropertyType[]).filter(type => homeType[type]).length;
    if (selectedCount > 0 && selectedCount < ALL_HOME_TYPES_COUNT) {
      parsed.homeType = homeType;
    }
  }

  for (const field of ['bedrooms', 'bathrooms', 'parkingSpaces'] as const) {
    const value = filter[field];
    if (value && value !== ANY_COUNT_VALUE) {
      parsed[field] = value;
    }
  }

  for (const field of ['priceMin', 'priceMax', 'sqftMin', 'sqftMax'] as const) {
    const value = filter[field];
    if (typeof value === 'number') {
      parsed[field] = value;
    }
  }

  for (const field of ['listedSince', 'listedTo'] as const) {
    const value = filter[field];
    if (value) {
      parsed[field] = value;
    }
  }

  for (const feature of BOOLEAN_FEATURES) {
    if (filter[feature]) {
      parsed[feature] = true;
    }
  }

  if (filter.rental) {
    parsed.rental = true;
  }

  if (filter.status && filter.status !== AVAILABLE_STATUS) {
    parsed.status = filter.status;
  }

  const locker = filter.additional?.condoOrTownhouse?.locker;
  const maintenanceFee = filter.additional?.condoOrTownhouse?.maintenanceFee;
  const condoOrTownhouse: { locker?: ParsedLocker; maintenanceFee?: number } = {};
  if (locker && locker !== ANY_LOCKER_VALUE) {
    condoOrTownhouse.locker = locker;
  }
  if (typeof maintenanceFee === 'number') {
    condoOrTownhouse.maintenanceFee = maintenanceFee;
  }
  if (Object.keys(condoOrTownhouse).length > 0) {
    parsed.additional = { condoOrTownhouse };
  }

  return parsed;
}

/**
 * Narrows the parser's free-form `sort` string to a platform `Sort`.
 *
 * Sort is not part of `Filter` — the page applies it through its own sort
 * handler — so it is mapped separately from {@link toFilterPatch}. Unknown
 * values yield `undefined` rather than being passed through, so a parser that
 * grows new sort keys cannot push an invalid one into the page.
 */
export function toSortValue(filters: ParsedSearchFilters): Sort | undefined {
  const { sort } = filters;
  if (sort && SORT_KEYS.includes(sort)) {
    return sort as Sort;
  }
  return undefined;
}

/**
 * A copy in random order. Fisher-Yates, so every ordering is equally likely.
 *
 * Shuffle once and hold the result — re-running it per render would rearrange
 * chips under the user's finger.
 */
export function shuffled<T>(items: readonly T[]): T[] {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/** Span fields a chip is built from, where the two do not share a name. */
const TOKEN_SPAN_FIELDS: Record<string, readonly string[]> = {
  price: ['priceMin', 'priceMax'],
  sqft: ['sqftMin', 'sqftMax'],
  locker: ['additional.condoOrTownhouse.locker'],
  maintenanceFee: ['additional.condoOrTownhouse.maintenanceFee'],
};

/** Introducers that only existed to attach the phrase being removed. */
const LEADING_CONNECTOR = /^\s*(?:,|and|with|plus|including)\b\s*/i;
const TRAILING_ARTICLE = /\s*\b(?:a|an|the)\s*$/i;
const TRAILING_CONNECTOR = /[\s,]*\b(?:with|and|plus|including|having)\s*$/i;

/** The spans that produced one chip. */
function spansForToken(
  spans: ReadonlyArray<MatchSpan>,
  tokenId: string
): MatchSpan[] {
  if (tokenId.startsWith(HOME_TYPE_TOKEN_PREFIX)) {
    const type = tokenId.slice(HOME_TYPE_TOKEN_PREFIX.length);
    return spans.filter(span => span.field === 'homeType' && span.value === type);
  }
  const fields = TOKEN_SPAN_FIELDS[tokenId] ?? [tokenId];
  return spans.filter(span => fields.includes(span.field));
}

/**
 * Cuts one matched phrase out, tidying the seam it leaves behind.
 *
 * Removing "pool" from "condos with a pool" has to take "with a" with it, or
 * the sentence is left hanging. But when the phrase was one of several — "with
 * pool and garage" — the introducer still belongs to what remains, and it is
 * the conjunction that has to go instead. Which side to trim is therefore
 * decided by whether anything follows.
 */
function cutPhrase(query: string, phrase: string): string {
  const start = query.toLowerCase().indexOf(phrase.toLowerCase());
  if (start === -1) {
    return query;
  }

  let prefix = query.slice(0, start);
  let suffix = query.slice(start + phrase.length);

  if (LEADING_CONNECTOR.test(suffix)) {
    suffix = suffix.replace(LEADING_CONNECTOR, ' ');
  } else {
    prefix = prefix.replace(TRAILING_ARTICLE, '').replace(TRAILING_CONNECTOR, '');
  }

  return `${prefix}${suffix}`.replace(/\s{2,}/g, ' ').replace(/\s+([,.])/g, '$1').trim();
}

/**
 * Takes the words behind a chip back out of the query.
 *
 * A chip is a reading of part of the sentence, so dismissing it has to edit the
 * sentence — otherwise the text still asks for a pool that is no longer being
 * filtered on, and the next parse puts the chip straight back.
 *
 * Phrases are located by text rather than by span offset: spans index the
 * normalized query, which can differ in length from what the user typed
 * ("three" parses as "3"). When the phrase cannot be found the query is
 * returned untouched, leaving the chip removal to stand on its own.
 */
export function removeTokenTextFromQuery(
  query: string,
  tokenId: string,
  spans: ReadonlyArray<MatchSpan>
): string {
  return spansForToken(spans, tokenId)
    .map(span => span.raw)
    .filter(Boolean)
    .reduce(cutPhrase, query);
}

/**
 * The place the query is currently naming, if any.
 *
 * Everything after the last location preposition, stopped at the next word that
 * starts a new clause, so "condos in Toronto with a pool" asks about "Toronto"
 * rather than the whole tail. The preposition is what makes this safe to run on
 * every keystroke: without one there is no reason to believe a bare word is a
 * place, and completing arbitrary words would put a location list in front of
 * someone typing "pool".
 *
 * Returns null when the query names no place or names one too short to look up
 * — Go-Search ignores fragments under three characters anyway.
 */
export function extractPlaceFragment(query: string): string | null {
  // Leading `.*` is greedy, so this anchors on the last preposition, not the
  // first: "in a condo in Toronto" asks about Toronto.
  const match = /^.*(?:^|\s)(?:in|near|around|at|within|close to)\s+(.*)$/i.exec(query);
  if (!match) {
    return null;
  }

  let fragment = match[1];
  const terminator = PLACE_FRAGMENT_TERMINATORS.exec(fragment);
  if (terminator) {
    fragment = fragment.slice(0, terminator.index);
  }

  fragment = fragment.trim();
  return fragment.length >= MIN_PLACE_FRAGMENT_LENGTH ? fragment : null;
}

/** True when the parse produced at least one applicable (non-location) filter. */
export function hasApplicableFilters(filters: ParsedSearchFilters): boolean {
  return Object.keys(filters).some(
    key => !LOCATION_FIELDS.includes(key as typeof LOCATION_FIELDS[number])
      && filters[key as keyof ParsedSearchFilters] !== undefined
  );
}
